import { useCallback, useMemo } from 'react';

import type { TextFieldProps } from '@mui/material/TextField';
import TextField from '@mui/material/TextField';
import { captureEvent } from '@/services/analytics';

import { debounce } from '@/utils/debounce';

export type PostHogTextFieldProps = TextFieldProps & {
  id: string;
};

const PostHogTextField = ({ id, value, onClick, onKeyDown, ...rest }: PostHogTextFieldProps) => {
  const debouncedCapture = useMemo(
    () =>
      debounce((inputValue: string) => {
        if (inputValue.trim() !== '') {
          captureEvent('gxgo_text_field_submitted', {
            id,
            searchValue: inputValue
          });
        }
      }, 2000),
    [id]
  );

  const capturePostHogSearch = useCallback(
    (inputValue: string) => {
      debouncedCapture(inputValue);
    },
    [debouncedCapture]
  );

  const getCurrentValue = (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;

    if (target && typeof target.value === 'string') {
      return target.value;
    }

    // Fallback to controlled value prop
    return typeof value === 'string' ? value : '';
  };

  const handleClick: TextFieldProps['onClick'] = event => {
    capturePostHogSearch(getCurrentValue(event as any));
    onClick?.(event);
  };

  const handleKeyDown: TextFieldProps['onKeyDown'] = event => {
    capturePostHogSearch(getCurrentValue(event as any));
    onKeyDown?.(event);
  };

  return (
    <TextField
      {...rest}
      id={id}
      value={value}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  );
};

export default PostHogTextField;
